import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PageLayout from "../components/PageLayout";
import {
  faChevronDown,
  faCircleNotch,
  faFileCircleMinus,
  faFileCirclePlus,
  faFilePen,
  faPenToSquare,
  faPlus,
  faRotate,
  faTrash,
  faUserMinus,
  faUserPen,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { IChaseLogs } from "../types/chase-log-types";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";
import moment from "moment";
import clsx from "clsx";

interface IDataType {
  totalCount: number;
  data: IChaseLogs[];
}

const logMeta: Record<string, { label: string; icon: IconDefinition; chip: string }> = {
  "chase-created": {
    label: "Chase created",
    icon: faPlus,
    chip: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  "chase-updated": {
    label: "Chase updated",
    icon: faPenToSquare,
    chip: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  },
  "chase-deleted": {
    label: "Chase deleted",
    icon: faTrash,
    chip: "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300",
  },
  "driver-created": {
    label: "Driver added",
    icon: faUserPlus,
    chip: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  "driver-updated": {
    label: "Driver updated",
    icon: faUserPen,
    chip: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  },
  "driver-deleted": {
    label: "Driver removed",
    icon: faUserMinus,
    chip: "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300",
  },
  "chase-item-created": {
    label: "Chase item added",
    icon: faFileCirclePlus,
    chip: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  "chase-item-updated": {
    label: "Chase item updated",
    icon: faFilePen,
    chip: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  },
  "chase-item-status-updated": {
    label: "Status updated",
    icon: faRotate,
    chip: "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
  "chase-item-deleted": {
    label: "Chase item removed",
    icon: faFileCircleMinus,
    chip: "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300",
  },
};

/** Plumbing that carries no meaning for someone reading the history. */
const hiddenKeys = new Set([
  "id",
  "chaseId",
  "chaseItemId",
  "driverId",
  "userId",
  "createdAt",
  "updatedAt",
  "deletedAt",
  "rId",
]);

const humanizeKey = (key: string) => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? JSON.stringify(value, null, 2) : "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);

  const asString = String(value);
  // ISO timestamps read better as dates
  if (/^\d{4}-\d{2}-\d{2}T/.test(asString)) {
    return moment(asString).format("DD MMM YYYY · HH:mm");
  }
  return asString.replace(/-/g, " ");
};

/** The changed fields worth showing, unwrapping Sequelize rows stored as-is. */
const getChangedFields = (newValues: Record<string, any>) => {
  const source = newValues?.dataValues ?? newValues ?? {};

  return Object.entries(source).filter(([key, value]) => {
    if (hiddenKeys.has(key)) return false;
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value) && !value.length) return false;
    return true;
  });
};

const ChaseLogs = () => {
  const id = window.location.pathname.split("/").pop();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IDataType>({ totalCount: 0, data: [] });
  const [query] = useState<string | null>(null);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number[]>([]);

  // ------------------------------------------------------------------ SEARCH CHASE LOGS -------------------------------------------------
  useEffect(() => {
    const findChaseLogs = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `/chase/logs/${id}?limit=${limit}&offset=${offset}${
            query ? `&search=${query}` : ""
          }`,
        );
        setData(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    id && findChaseLogs();
  }, [id, query]);

  // --------------------------------------------------------------- GET MORE CHASE LOGS ------------------------------------------------
  const findMoreLogs = async () => {
    setLoading(true);
    try {
      setOffset((prev) => prev + limit);
      const res = await axiosInstance.get(
        `/chase/logs/${id}?offset=${offset + limit}&limit=${limit}`,
      );
      setData((prev) => ({
        totalCount: prev.totalCount,
        data: [...prev.data, ...res.data.data],
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // ------------------------------------------- TOGGLE LOG DETAILS ------------------------------------------------

  const toggleDetails = (logId: number) => {
    setExpanded((prev) =>
      prev.includes(logId)
        ? prev.filter((item) => item !== logId)
        : [...prev, logId],
    );
  };

  return (
    <PageLayout pageTitle="Chase logs" primaryAction={<div></div>}>
      <div className="mt-5 mr-1 bg-surface border border-edge rounded-2xl shadow-card px-6 pt-6 pb-5 animate-fade-in smallTablet:mr-0 smallTablet:mt-3 smallTablet:px-4 smallTablet:pt-4">
        <div className="flex w-full items-start justify-between gap-4 flex-wrap mb-5">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-text-primary leading-tight smallTablet:text-[18px]">
              Chase logs
            </h1>
            <p className="mt-1 text-[13px] font-medium text-light-gray smallTablet:break-all">
              {id}
              <span> · {new Intl.NumberFormat("en-GB").format(data?.totalCount ?? 0)} events</span>
            </p>
          </div>
        </div>

        {/* ---------------------------------------------------------- LOGS CONTAINER ----------------------------------------- */}
        <div className="w-full">
          {data?.data?.map((item, index) => {
            const meta = logMeta[item?.type] ?? {
              label: item?.type?.replaceAll("-", " "),
              icon: faPenToSquare,
              chip: "bg-overlay-mid text-light-gray",
            };
            const changes = getChangedFields(item?.newValues);
            const logId = item?.id ?? index;
            const isOpen = expanded.includes(logId);

            return (
              <div
                key={logId}
                className="border-b border-edge-faint last:border-none"
              >
                <div className="flex items-center gap-3 py-3.5 smallTablet:flex-wrap smallTablet:gap-2">
                  <div
                    className={clsx(
                      "h-9 w-9 shrink-0 rounded-xl flex items-center justify-center",
                      meta.chip,
                    )}
                  >
                    <FontAwesomeIcon icon={meta.icon} className="text-[13px]" />
                  </div>

                  <div className="min-w-0 smallTablet:flex-1">
                    <p className="text-[14px] font-semibold text-text-primary capitalize">
                      {meta.label}
                    </p>
                    <p className="mt-0.5 text-[12px] font-medium text-light-gray">
                      by {item?.user?.name ?? "Unknown"}
                    </p>
                  </div>

                  {item?.type === "chase-item-status-updated" &&
                  item?.newValues?.status ? (
                    <span className="ml-3 smallTablet:ml-0 shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-overlay-mid text-text-primary capitalize">
                      {String(item.newValues.status).replace(/-/g, " ")}
                    </span>
                  ) : null}

                  <p className="ml-auto shrink-0 text-[12px] font-medium text-light-gray tabular-nums whitespace-nowrap smallTablet:text-[11px]">
                    {moment(item?.createdAt).format("DD MMM YYYY · HH:mm")}
                  </p>

                  {changes.length ? (
                    <button
                      type="button"
                      title={isOpen ? "Hide changes" : "Show changes"}
                      aria-expanded={isOpen}
                      onClick={() => toggleDetails(logId)}
                      className="shrink-0 h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={clsx(
                          "text-[12px] transition-transform duration-200",
                          isOpen && "rotate-180",
                        )}
                      />
                    </button>
                  ) : (
                    <span className="shrink-0 w-7" />
                  )}
                </div>

                {/* ------------------------------------ WHAT CHANGED ------------------------------------ */}
                {isOpen && changes.length ? (
                  <div className="pb-4 animate-fade-in">
                    <p className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em] mb-3">
                      New values
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {changes.map(([key, value]) => {
                        const formatted = formatValue(value);
                        const isBlock = formatted.includes("\n");

                        return (
                          <div
                            key={key}
                            className="bg-surface-muted border border-edge-faint rounded-xl px-3.5 py-2.5 min-w-0"
                          >
                            <p className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.08em]">
                              {humanizeKey(key)}
                            </p>
                            {isBlock ? (
                              <pre className="mt-1 text-[12px] font-medium text-text-primary whitespace-pre-wrap break-words max-h-[160px] overflow-y-auto custom-scrollbar">
                                {formatted}
                              </pre>
                            ) : (
                              <p className="mt-1 text-[13px] font-semibold text-text-primary capitalize break-words">
                                {formatted}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {loading ? (
            <div className="w-full h-[120px] flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCircleNotch}
                className="fa-spin text-light-gray text-[22px]"
              />
            </div>
          ) : null}

          {!loading && !data?.data?.length ? (
            <div className="w-full py-12 flex flex-col items-center justify-center gap-2 text-center">
              <div className="h-11 w-11 rounded-xl bg-overlay-mid flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faFilePen}
                  className="text-[16px] text-light-gray"
                />
              </div>
              <p className="text-[13px] font-medium text-light-gray">
                No activity recorded for this chase yet.
              </p>
            </div>
          ) : null}

          {data?.data?.length < data?.totalCount && data?.totalCount ? (
            <div className="flex justify-center pt-5">
              <button
                type="button"
                onClick={findMoreLogs}
                disabled={loading}
                className="h-10 px-5 rounded-full border border-edge text-[13px] font-semibold text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200 disabled:opacity-50"
              >
                Load more events
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </PageLayout>
  );
};

export default ChaseLogs;
